<template>
  <div class="card-member" v-on="$listeners">
    <card-user
      can-user-name-click
      v-bind:user-id="userId"
      v-bind:user-name="userName"
      v-bind:groups="groups"
      v-bind:history="history"
      v-bind:base-date="baseDate"
      v-bind:profile-image-attach-file-type="'9'"
      v-bind:role-id="roleId"
      v-on="$listeners"
    />
    <div
      v-if="!simple && items.length"
      class="items-container"
      v-bind:style="{ '--background-color': itemBackgroundColor }"
    >
      <component
        v-for="item in itemsModified"
        v-bind:key="item.itemType"
        v-bind:is="item.component"
        v-bind="item"
      />
    </div>
  </div>
</template>

<script>
import CardUser from '@/components/AppCardUser.vue'
import {
  CardMemberItemAccuracy,
  CardMemberItemAccuracyRating,
  CardMemberItemActivity,
  CardMemberItemBattery,
  CardMemberItemIndoor,
  CardMemberItemPlace,
  CardMemberItemPositioning,
  CardMemberItemRegistration,
  CardMemberItemSensorData,
  CardMemberItemStatus,
  CardMemberItemUserAttribute,
} from '@/components/member/carditem'
import { ITEM_TYPE } from '@/composables/member/UseUserStatusAdditionalItems'
import StringUtil from '@/js/util/StringUtil.js'

/**
 * item_type と描画コンポーネントの対応
 */
const TYPE_COMPONENT_MAP = new Map([
  [ITEM_TYPE.ACCURACY, CardMemberItemAccuracy],
  [ITEM_TYPE.ACCURACY_RATING, CardMemberItemAccuracyRating],
  [ITEM_TYPE.ACTIVITY, CardMemberItemActivity],
  [ITEM_TYPE.ATTRIBUTE, CardMemberItemUserAttribute],
  [ITEM_TYPE.BATTERY, CardMemberItemBattery],
  [ITEM_TYPE.INDOOR, CardMemberItemIndoor],
  [ITEM_TYPE.PLACE, CardMemberItemPlace],
  [ITEM_TYPE.SENSOR_DATA, CardMemberItemSensorData],
  [ITEM_TYPE.STATUS, CardMemberItemStatus],
  [ITEM_TYPE.POSITIONING, CardMemberItemPositioning],
  [ITEM_TYPE.REGISTRATION, CardMemberItemRegistration],
  // 値と項目名を並べるだけなので、プレイスカードの固定項目と同様にユーザー属性の部品を流用する
  [ITEM_TYPE.DEVICE, CardMemberItemUserAttribute],
  [ITEM_TYPE.SENSOR_NAMES, CardMemberItemUserAttribute],
])

export default {
  name: 'AppCardMember',
  components: {
    CardUser,
  },
  inheritAttrs: false,
  props: {
    /**
     * ユーザーID
     */
    userId: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * ユーザー名
     */
    userName: {
      type: String,
      default: null,
    },
    /**
     * 所属グループ情報
     */
    groups: {
      type: Array,
      default: () => [],
    },
    /**
     * 履歴ボタンが使用可能な場合はtrue
     */
    history: {
      type: Boolean,
      default: false,
    },
    /**
     * 相対時間を計算する際の基準日時
     */
    baseDate: {
      type: Date,
      default: null,
    },
    /**
     * 簡易表示の場合はtrue
     */
    simple: {
      type: Boolean,
      default: false,
    },
    /**
     * 追加表示アイテム
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * ユーザーのロールID
     */
    roleId: {
      type: Number,
      default: null,
    },
  },
  data: function () {
    return {
      // アイテム部の背景色
      itemBackgroundColor: '#f6f6f6',
      // 1remのサイズ(px)
      remSize: 16,
    }
  },
  computed: {
    /**
     * 表示用に加工したアイテム
     * @type {Array.<Object>}
     */
    itemsModified: function () {
      return this.items.map((item) => {
        // 対象コンポーネント取得
        const type = item.item_type.split(':')[0]
        const result = { component: TYPE_COMPONENT_MAP.get(type) }

        // キーを camelCase 化
        for (const [key, value] of Object.entries(item)) {
          const camelKey = StringUtil.snakeToCamel(key)
          result[camelKey] = value
        }

        // 個別値追加
        if (type === ITEM_TYPE.STATUS) {
          result.backgroundColor = this.itemBackgroundColor
        } else if (type === ITEM_TYPE.ACTIVITY) {
          result.baseDate = this.baseDate
        } else if (type === ITEM_TYPE.ATTRIBUTE) {
          result.remSize = this.remSize
        } else if (type === ITEM_TYPE.SENSOR_DATA) {
          result.remSize = this.remSize
        } else if (type === ITEM_TYPE.POSITIONING) {
          result.baseDate = this.baseDate
        } else if (type === ITEM_TYPE.REGISTRATION) {
          result.baseDate = this.baseDate
        } else if (type === ITEM_TYPE.DEVICE) {
          // 紐付けが無い場合は device_name が null。空文字で返ってきても "-" になるよう falsy で判定する
          result.value = item.device_name || null
          result.attributeName = this.$t('appCardMember.itemName.device')
          result.attributeType = null
          result.remSize = this.remSize
        } else if (type === ITEM_TYPE.SENSOR_NAMES) {
          // names は複数のセンサー名をAPI側でカンマ連結した文字列。紐付けが無い場合は null
          result.value = item.names || null
          result.attributeName = this.$t('appCardMember.itemName.sensorNames')
          result.attributeType = null
          result.remSize = this.remSize
        }

        return result
      })
    },
  },
  created: function () {
    this.$_getRemSize()
  },
  methods: {
    /**
     * 1remのサイズ(px)を取得する
     */
    $_getRemSize: function () {
      const fontSize = getComputedStyle(document.documentElement).fontSize
      this.remSize = +fontSize.replace('px', '')
    },
  },
}
</script>

<style lang="scss" scoped>
.card-member {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 10px;
}

.items-container {
  --background-color: #f6f6f6;
  width: 100%;
  padding: 8px;
  background-color: var(--background-color);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-auto-rows: max-content;
  justify-items: flex-start;
  row-gap: 8px;
  column-gap: 8px;
}
</style>
